import { isNotNull, OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { HealthcheckStatusResult, HealthcheckType } from '../types'
import { useMultiPayGroupsFormStatus } from './LocationsSurvey'
import { getOnboardingStepAssignee } from '../utils/onboardingWorkflowUtils'

export const useLocationsStatus = (
  org: OrganizationDetailFragment,
  onboardingWorkflow?: OrganizationDetailFragment['onboardingWorkflow']
): HealthcheckStatusResult => {
  const formStatus = useMultiPayGroupsFormStatus(org)
  const locations = org.workLocations.filter(isNotNull)
  const payGroups = org.employers.filter(isNotNull)
  const hasLocations = locations.length > 0
  const locationsWithoutPayGroups = locations.filter((location) => !location.employersInfo?.length)
  const hasLocationWithoutPayGroup = locationsWithoutPayGroups.length > 0
  const assignee = getOnboardingStepAssignee(onboardingWorkflow, OnboardingStepType.WorkLocations)

  const healthcheckTitle = 'Locations & pay groups'

  const baseProperties = {
    assignee,
    healthcheckTitle,
    healthcheckType: HealthcheckType.WorkLocations,
    primary: healthcheckTitle,
  } as const

  if (formStatus !== 'complete' && !org.isOnboarded) {
    return {
      status: 'incomplete',
      secondary: 'Let us know about your multi-pay group process',
      ...baseProperties,
    }
  }

  if (!hasLocations) {
    return {
      status: 'incomplete',
      secondary: 'Add locations & pay groups',
      ...baseProperties,
    }
  }

  if (hasLocationWithoutPayGroup) {
    return {
      status: 'pending',
      secondary: `Assign a pay group to your location${locationsWithoutPayGroups.length > 1 ? 's' : ''}`,
      ...baseProperties,
    }
  }

  const oneLocation = locations.length === 1
  return {
    status: 'complete',
    secondary: `${locations.length} location${oneLocation ? '' : 's'}, ${payGroups.length} pay group${payGroups.length > 1 ? 's' : ''}`,
    ...baseProperties,
  }
}
